import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { CheckCircle } from "@phosphor-icons/react";

const inp = "w-full h-9 px-3 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none focus:border-equipr-black";

export default function ResetPassword() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/verify-token?token=${encodeURIComponent(token)}`)
      .then(({ data }) => { setInfo(data); setState("valid"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/set-password", { token, password: pw });
      setState("done");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Something went wrong.");
    }
    setSaving(false);
  };

  return (
    <div className="h-screen w-full flex bg-equipr-black">
      <div className="hidden md:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-equipr-yellow/10 rounded-full blur-3xl" />
        <BrandLogo variant="dark" />
        <div className="relative z-10">
          <h1 className="text-white text-4xl font-extrabold leading-tight">Welcome<br /><span className="text-equipr-yellow">aboard.</span></h1>
          <p className="text-gray-400 mt-4 max-w-sm text-sm">Set your password to activate your EQUIPR Civil account and get started.</p>
        </div>
        <p className="text-gray-600 text-xs relative z-10">© EQUIPR Software</p>
      </div>

      <div className="flex-1 flex items-center justify-center bg-white p-6">
        <div className="w-full max-w-sm" data-testid="reset-page">
          <div className="md:hidden mb-8"><BrandLogo /></div>

          {state === "checking" && <p className="text-sm text-gray-500 animate-pulse" data-testid="reset-checking">Verifying your link…</p>}

          {state === "invalid" && (
            <div data-testid="reset-invalid">
              <h2 className="text-2xl font-bold text-equipr-black">Link expired</h2>
              <p className="text-sm text-gray-500 mb-6 mt-2">This link is invalid or has expired. Please ask an administrator to send you a new invite or reset link.</p>
              <button onClick={() => navigate("/login")} className="h-10 px-5 bg-equipr-black text-white font-bold text-sm rounded-none hover:bg-gray-800">Back to Sign In</button>
            </div>
          )}

          {state === "valid" && (
            <form onSubmit={submit} data-testid="reset-form">
              <h2 className="text-2xl font-bold text-equipr-black">{info?.kind === "invite" ? "Set your password" : "Reset your password"}</h2>
              <p className="text-sm text-gray-500 mb-6 mt-1">for <b>{info?.email}</b></p>
              {error && <div className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="reset-error">{error}</div>}
              <label className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block">New Password</label>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password-input" className={inp + " mb-4"} placeholder="At least 8 characters" />
              <label className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block">Confirm Password</label>
              <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm" className={inp + " mb-6"} placeholder="Re-enter password" />
              <button type="submit" disabled={saving} data-testid="reset-submit" className="w-full h-10 bg-equipr-yellow text-equipr-black font-bold text-sm rounded-none hover:bg-equipr-yellow-dark disabled:opacity-60">
                {saving ? "Saving…" : "Set Password"}
              </button>
            </form>
          )}

          {state === "done" && (
            <div data-testid="reset-done">
              <CheckCircle size={40} weight="fill" className="text-green-600 mb-3" />
              <h2 className="text-2xl font-bold text-equipr-black">All set!</h2>
              <p className="text-sm text-gray-500 mb-6 mt-2">Your password has been saved. You can now sign in to EQUIPR Civil.</p>
              <button onClick={() => navigate("/login")} data-testid="reset-goto-login" className="h-10 px-5 bg-equipr-yellow text-equipr-black font-bold text-sm rounded-none hover:bg-equipr-yellow-dark">Go to Sign In</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
